import type { LucideIcon } from 'lucide-react';
import type { MouseEvent } from 'react';
import { hasPermission, isAdmin, useAuthData } from '@auth/features/auth';
import { useClerk, useSession, useUser } from '@clerk/clerk-react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { Role } from '@travel-planner/shared/schemas';
import {
  ChevronLeft,
  ChevronRight,
  CircleGauge,
  ClipboardList,
  Inbox as InboxIcon,
  // LayoutDashboard, // Ops Dashboard v2 menu entry — hidden for now (see navItems)
  LogOut,
  Map as MapIcon,
  Megaphone,
  Package,
  Users,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import { trpc } from '@/shared/api/trpc';
import { getInitials } from '@/shared/lib/get-initials';
import { Avatar, Button, NavItem, Text, Tooltip } from '@/shared/ui';
import { DEFAULT_LOGO_URL, useOrgLogoUrl } from '../lib/use-org-logo';
import css from './sidebar.module.scss';

type SidebarProps = {
  activeItem?: string;
  collapsed?: boolean;
  onToggle?: () => void;
};

type LeafNavItem = {
  id: string;
  label: string;
  path: string;
};

type NavItemDef = {
  id: string;
  label: string;
  icon: LucideIcon;
  // Leaves navigate on click; parents expand a static sub-list. Each
  // item has either `path` or `children`, never both.
  path?: string;
  children?: LeafNavItem[];
  // Set when the item should only render for users matching a role check.
  adminOnly?: boolean;
  // Set when the item should only render for users holding one of these roles.
  requiredRoles?: string[];
  // Set when the item should only render for users holding this permission.
  requiredPermission?: { resource: string; action: string };
  /** Shown only when `marketing.hub.canEnter` says this account may enter. */
  requiresMarketingHub?: boolean;
  // Set when a plain click should open the page in a new browser tab instead of
  // navigating this one (the Marketing HUB is its own portal).
  newTab?: boolean;
  // but filtered out of the rendered nav. Unset this to re-enable.
  hidden?: boolean;
};

const navItems: NavItemDef[] = [
  {
    id: 'consultant-dashboard',
    label: 'Moodboard',
    icon: CircleGauge,
    path: '/moodboard',
  },
  {
    // Temporarily hidden — Inbox functionality is incomplete. The /inbox
    // route redirects to /itineraries (see `app/routes/inbox.ts`). Remove
    // `hidden` here + the route's `beforeLoad` redirect to re-enable.
    id: 'inbox',
    label: 'Inbox',
    icon: InboxIcon,
    path: '/inbox',
    hidden: true,
  },
  {
    id: 'itineraries',
    label: 'Itineraries',
    icon: MapIcon,
    path: '/itineraries',
  },
  {
    id: 'products',
    label: 'Products',
    icon: Package,
    path: '/products',
  },
  {
    id: 'operations',
    label: 'Operations',
    icon: ClipboardList,
    path: '/operations',
  },
  // Ops Dashboard v2 (XT-225) is on dev for testing but hidden from the menu
  // for now — reach it directly at /operations/dashboard-v2. Restore this entry
  // (and the `LayoutDashboard` import) to surface it in the sidebar.
  // {
  //   id: 'operations-dashboard',
  //   label: 'Ops Dashboard',
  //   icon: LayoutDashboard,
  //   path: '/operations/dashboard-v2',
  // },
  {
    id: 'crm',
    label: 'Crm',
    icon: Users,
    path: '/crm',
  },
  {
    // XT-808 O2 — own top-level item (not under CRM). For now only `system`
    // accounts see the link (product owner, XT-1000), and only when the hub
    // lets them in (`marketing.hub.canEnter`); the hub enforces its role
    // matrix server-side for anyone who opens /marketing directly.
    id: 'marketing',
    label: 'Marketing',
    icon: Megaphone,
    path: '/marketing',
    requiredRoles: [Role.System],
    requiresMarketingHub: true,
    newTab: true,
  },
];

// Leaves normally route through the SPA on a plain click; `newTab` leaves are
// left to the browser as a real `<a target="_blank">` (new tab, no opener).
const leafLinkProps = (
  newTab: boolean | undefined,
  onClick: (e: MouseEvent<HTMLAnchorElement>) => void
) => (newTab ? { target: '_blank', rel: 'noopener noreferrer' } : { onClick });

export const Sidebar = ({
  activeItem = 'crm',
  collapsed = false,
  onToggle,
}: SidebarProps) => {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Items render as real `<a href>` so the browser offers "Open in new tab",
  // middle-click, and Cmd/Ctrl+click. A plain left-click is intercepted and
  // routed through the SPA router (no full reload); modified/non-primary
  // clicks fall through to the browser's native anchor handling.
  const handleNavClick = useCallback(
    (path: string) => (e: MouseEvent<HTMLAnchorElement>) => {
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      ) {
        return;
      }
      e.preventDefault();
      // biome-ignore lint/suspicious/noExplicitAny: cross-domain route reference
      navigate({ to: path as any });
    },
    [navigate]
  );
  const { signOut } = useClerk();
  const { session } = useSession();
  const { user: clerkUser } = useUser();
  const { data: authData } = useAuthData();
  const authUser = authData?.user;
  const logoUrl = useOrgLogoUrl();
  // When the resolved org logo fails to load, fall back to the brand default so
  // a logo always shows (see DEFAULT_LOGO_URL). Keyed on the failed URL, not a
  // boolean, so switching orgs re-attempts the new org's logo automatically.
  const [failedLogoUrl, setFailedLogoUrl] = useState<string | null>(null);
  const logoSrc = logoUrl === failedLogoUrl ? DEFAULT_LOGO_URL : logoUrl;
  const callerIsAdmin = isAdmin(authUser?.roles ?? []);
  const signOutMutation = trpc.auth.signOut.useMutation();
  // Only a `system` account can see the Marketing item, so nobody else asks.
  const marketingHub = trpc.marketing.hub.canEnter.useQuery(undefined, {
    enabled: (authUser?.roles ?? []).includes(Role.System),
    retry: false,
    staleTime: 5 * 60_000,
  });

  const visibleNavItems = navItems.filter(
    (item) =>
      !item.hidden &&
      (!item.adminOnly || callerIsAdmin) &&
      (!item.requiresMarketingHub || marketingHub.data?.canEnter === true) &&
      (!item.requiredRoles ||
        (authUser?.roles ?? []).some((r) => item.requiredRoles?.includes(r))) &&
      (!item.requiredPermission ||
        hasPermission(
          authUser?.permissions ?? [],
          item.requiredPermission.resource as Parameters<
            typeof hasPermission
          >[1],
          item.requiredPermission.action as Parameters<typeof hasPermission>[2]
        ))
  );

  const firstName = authUser?.firstName ?? clerkUser?.firstName ?? '';
  const lastName = authUser?.lastName ?? clerkUser?.lastName ?? '';
  const email =
    authUser?.email ?? clerkUser?.primaryEmailAddress?.emailAddress ?? '';
  const initials = getInitials(firstName, lastName, email);
  const fullName = [firstName, lastName].filter(Boolean).join(' ') || 'User';

  const handleSignOut = useCallback(async () => {
    if (session?.id) {
      await signOutMutation
        .mutateAsync({ sessionId: session.id })
        .catch(() => {});
    }
    signOut({ redirectUrl: '/sign-in' });
  }, [signOut, session?.id, signOutMutation]);

  return (
    <aside className={collapsed ? css.rootCollapsed : css.root}>
      <div className={css.logo}>
        <img
          alt="Logo"
          className={css.logoImg}
          onError={() => setFailedLogoUrl(logoUrl)}
          src={logoSrc}
        />
      </div>
      <nav className={css.nav}>
        {visibleNavItems.map((item) => {
          const { id, label, icon: Icon, path, children } = item;
          if (children && children.length > 0) {
            // Parent item: expanded sub-list when sidebar is open; in
            // collapsed mode, the first leaf's path is the click target
            // so the icon-only rail still navigates somewhere sensible.
            const firstChildPath = children[0].path;
            const isParentActive = children.some((c) =>
              pathname.startsWith(c.path.replace(/\/[^/]+$/, ''))
            );
            return collapsed ? (
              <Tooltip content={label} key={id} position="right">
                <NavItem
                  active={isParentActive}
                  aria-label={label}
                  className={css.collapsedItem}
                  href={firstChildPath}
                  icon={<Icon style={{ height: 20, width: 20 }} />}
                  onClick={handleNavClick(firstChildPath)}
                />
              </Tooltip>
            ) : (
              <div className={css.group} key={id}>
                <div className={css.groupLabel}>
                  <Icon style={{ height: 14, width: 14 }} />
                  <Text as="span" size="xs" weight="medium">
                    {label}
                  </Text>
                </div>
                {children.map((child) => (
                  <NavItem
                    active={child.id === activeItem}
                    className={css.childItem}
                    href={child.path}
                    key={child.id}
                    onClick={handleNavClick(child.path)}
                  >
                    {child.label}
                  </NavItem>
                ))}
              </div>
            );
          }

          // Leaf item
          const leafPath = path ?? '/';
          return collapsed ? (
            <Tooltip content={label} key={id} position="right">
              <NavItem
                active={id === activeItem}
                aria-label={label}
                className={css.collapsedItem}
                href={leafPath}
                icon={<Icon style={{ height: 20, width: 20 }} />}
                {...leafLinkProps(item.newTab, handleNavClick(leafPath))}
              />
            </Tooltip>
          ) : (
            <NavItem
              active={id === activeItem}
              href={leafPath}
              icon={<Icon style={{ height: 20, width: 20 }} />}
              key={id}
              {...leafLinkProps(item.newTab, handleNavClick(leafPath))}
            >
              {label}
            </NavItem>
          );
        })}
      </nav>

      {/* Desktop: collapse toggle */}
      <div className={css.footer}>
        <Button
          icon={
            collapsed ? (
              <ChevronRight style={{ height: 16, width: 16 }} />
            ) : (
              <ChevronLeft style={{ height: 16, width: 16 }} />
            )
          }
          onClick={onToggle}
          size="icon"
          variant="ghost"
        />
      </div>

      {/* Mobile: user profile + sign out */}
      {!collapsed && (
        <div className={css.userSection}>
          <div className={css.userInfo}>
            <Avatar initials={initials} size="md" />
            <div className={css.userDetails}>
              <Text as="span" size="sm" weight="medium">
                {fullName}
              </Text>
              <Text as="span" className={css.userEmail} size="xs">
                {email}
              </Text>
            </div>
          </div>
          <Button
            aria-label="Sign out"
            icon={<LogOut style={{ height: 16, width: 16 }} />}
            onClick={handleSignOut}
            size="icon"
            variant="ghost"
          />
        </div>
      )}
    </aside>
  );
};
